// Document-level query service: allows review teams to raise queries on specific documents
// and hospital to reply/resolve with updated documents.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  arrayUnion,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { sendEmail } from './notificationService';
import { getApplication } from './applicationService';
import { getPatientApplicationLinkHtml } from '../utils/emailUtils';
import { sendQueryRaisedPushNotification, sendQueryResolvedPushNotification } from './pushNotificationService';

// Raise a query on a specific document
// documentKey: key in application.documents map (e.g., 'aadharCard', 'policyDocument')
// raisedBy: { uid, name, email, team } - team can be 'doctor', 'finance', 'policy'
export const raiseDocumentQuery = async (applicationId, documentKey, comment, raisedBy) => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) return { success: false, error: 'Application not found' };
    const app = appResult.application;

    // Ensure documentKey exists in application.documents
    const docExists = app.documents && app.documents[documentKey];
    if (!docExists) {
      return { success: false, error: `Document key '${documentKey}' not found` };
    }

    const queryData = {
      applicationId: app.applicationId,
      documentKey,
      documentTitle: app.documents[documentKey]?.fileName || documentKey,
      comment: comment.trim(),
      status: 'open', // open | resolved
      raisedBy: {
        uid: raisedBy.uid,
        name: raisedBy.name || raisedBy.email,
        email: raisedBy.email,
        team: raisedBy.team || 'doctor', // doctor | finance | policy
      },
      raisedAt: serverTimestamp(),
      replies: [], // [{ userId, userName, message, timestamp, isHospital }]
      resolvedAt: null,
      resolvedBy: null,
      updatedDocumentUrl: null, // URL of re-uploaded document
      updatedDocumentFileName: null,
    };

    // Add to documentQueries subcollection
    const queriesRef = collection(db, 'patientApplications', app.id, 'documentQueries');
    const queryDoc = await addDoc(queriesRef, queryData);

    // Update application to flag that queries exist
    const appRef = doc(db, 'patientApplications', app.id);
    await updateDoc(appRef, {
      hasDocumentQueries: true,
      updatedAt: serverTimestamp(),
    });

    // Update bucket filter - query raised
    // Need to import updateRoleBucket at the top
    const { updateRoleBucket } = await import('./bucketFilterService');
    await updateRoleBucket(app.id, 'doctor', 'query_raised');
    await updateRoleBucket(app.id, 'hospital', 'query_raised');

    // Email notification to hospital
    try {
      const hospitalEmail = app.hospitalEmail || app.hospital?.email;
      if (hospitalEmail) {
        const patientName = app.patient?.name || 'Patient';
        const patientLink = getPatientApplicationLinkHtml(
          app.applicationId,
          patientName,
          hospitalEmail,
          'hospital',
          `View Patient Application for ${patientName} (${app.applicationId})`
        );
        await sendEmail({
          to: hospitalEmail,
          subject: `Document Query Raised - ${app.applicationId}`,
          html: `
            <h3>Document Query on Application ${app.applicationId}</h3>
            <p><strong>Document:</strong> ${queryData.documentTitle}</p>
            <p><strong>Raised by:</strong> ${raisedBy.name} (${raisedBy.team} team)</p>
            <p><strong>Comment:</strong> ${comment}</p>
            <p>Please log in to respond and upload updated document if required.</p>
            ${patientLink}
          `,
        });
      }

      // Send push notification
      try {
        await sendQueryRaisedPushNotification(
          applicationId,
          queryDoc.id,
          raisedBy.name || 'System',
          queryData.documentTitle || documentKey
        );
      } catch (pushErr) {
        console.warn('Push notification failed:', pushErr);
      }
    } catch (emailErr) {
      console.warn('Email notification failed:', emailErr);
    }

    return {
      success: true,
      queryId: queryDoc.id,
      message: 'Query raised successfully',
    };
  } catch (error) {
    console.error('Error raising document query:', error);
    return { success: false, error: error.message };
  }
};

// Reply to an existing document query (can be hospital or review team)
export const replyToDocumentQuery = async (applicationId, queryId, replyMessage, repliedBy) => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) return { success: false, error: 'Application not found' };
    const app = appResult.application;

    const queryRef = doc(db, 'patientApplications', app.id, 'documentQueries', queryId);
    const querySnap = await getDoc(queryRef);
    if (!querySnap.exists()) {
      return { success: false, error: 'Query not found' };
    }

    const reply = {
      userId: repliedBy.uid,
      userName: repliedBy.name || repliedBy.email,
      message: replyMessage.trim(),
      timestamp: Timestamp.now(),
      isHospital: repliedBy.team === 'hospital',
    };

    await updateDoc(queryRef, {
      replies: arrayUnion(reply),
    });

    // Notify review team if hospital replied
    if (repliedBy.team === 'hospital') {
      try {
        const queryData = querySnap.data();
        const raiserEmail = queryData.raisedBy?.email;
        if (raiserEmail) {
          const patientName = app.patient?.name || 'Patient';
          const team = queryData.raisedBy?.team || 'doctor';
          const patientLink = getPatientApplicationLinkHtml(
            app.applicationId,
            patientName,
            raiserEmail,
            team === 'doctor' ? 'doctor' : team === 'finance' ? 'finance' : 'admin',
            `View Patient Application for ${patientName} (${app.applicationId})`
          );
          await sendEmail({
            to: raiserEmail,
            subject: `Hospital Replied to Document Query - ${app.applicationId}`,
            html: `
              <h3>Hospital Reply on Application ${app.applicationId}</h3>
              <p><strong>Document:</strong> ${queryData.documentTitle}</p>
              <p><strong>Reply from:</strong> ${repliedBy.name}</p>
              <p><strong>Message:</strong> ${replyMessage}</p>
              <p>Log in to view the full conversation.</p>
              ${patientLink}
            `,
          });
        }
      } catch (emailErr) {
        console.warn('Email notification failed:', emailErr);
      }
    }

    return { success: true, message: 'Reply added successfully' };
  } catch (error) {
    console.error('Error replying to document query:', error);
    return { success: false, error: error.message };
  }
};

// Resolve a document query (hospital uploads updated document)
// newDocumentUrl: download URL of the newly uploaded document
export const resolveDocumentQuery = async (applicationId, queryId, newDocumentUrl, newDocumentFileName, resolvedBy) => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) return { success: false, error: 'Application not found' };
    const app = appResult.application;

    const queryRef = doc(db, 'patientApplications', app.id, 'documentQueries', queryId);
    const querySnap = await getDoc(queryRef);
    if (!querySnap.exists()) {
      return { success: false, error: 'Query not found' };
    }

    const queryData = querySnap.data();

    await updateDoc(queryRef, {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
      resolvedBy: {
        uid: resolvedBy.uid,
        name: resolvedBy.name || resolvedBy.email,
      },
      updatedDocumentUrl: newDocumentUrl,
      updatedDocumentFileName: newDocumentFileName,
    });

    // Optionally update the document in application.documents map
    const appRef = doc(db, 'patientApplications', app.id);
    const docKey = queryData.documentKey;
    const updatePayload = {};
    updatePayload[`documents.${docKey}.url`] = newDocumentUrl;
    updatePayload[`documents.${docKey}.fileName`] = newDocumentFileName;
    updatePayload[`documents.${docKey}.updatedAt`] = serverTimestamp();
    updatePayload.updatedAt = serverTimestamp();

    await updateDoc(appRef, updatePayload);

    // Update bucket filter - query resolved/reply received
    // Determine which phase based on workflow state
    const { updateRoleBucket } = await import('./bucketFilterService');
    const workflow = app.workflow || {};
    const kypGenerated = workflow.medicalVerification?.kypGenerated;
    const dischargeInitiated = app.dischargeInitiated || workflow.dischargeSettlement?.dischargeInitiated;
    const scrutinySubmitted = workflow.dischargeSettlement?.scrutinySubmission?.status === 'submitted';

    if (scrutinySubmitted) {
      await updateRoleBucket(app.id, 'doctor', 'scrutiny_query_reply_received');
    } else if (dischargeInitiated) {
      await updateRoleBucket(app.id, 'doctor', 'discharge_query_reply_received');
    } else if (kypGenerated) {
      await updateRoleBucket(app.id, 'doctor', 'kyp_query_reply_received');
    }
    await updateRoleBucket(app.id, 'hospital', 'query_resolved');

    // Notify review team
    try {
      const raiserEmail = queryData.raisedBy?.email;
      if (raiserEmail) {
        const patientName = app.patient?.name || 'Patient';
        const team = queryData.raisedBy?.team || 'doctor';
        const patientLink = getPatientApplicationLinkHtml(
          app.applicationId,
          patientName,
          raiserEmail,
          team === 'doctor' ? 'doctor' : team === 'finance' ? 'finance' : 'admin',
          `View Patient Application for ${patientName} (${app.applicationId})`
        );
        await sendEmail({
          to: raiserEmail,
          subject: `Document Query Resolved - ${app.applicationId}`,
          html: `
            <h3>Document Query Resolved on Application ${app.applicationId}</h3>
            <p><strong>Document:</strong> ${queryData.documentTitle}</p>
            <p><strong>Resolved by:</strong> ${resolvedBy.name} (Hospital)</p>
            <p>An updated document has been uploaded. Please review.</p>
            <p><a href="${newDocumentUrl}">View Updated Document</a></p>
            ${patientLink}
          `,
        });
      }

      // Send push notification
      try {
        await sendQueryResolvedPushNotification(
          applicationId,
          queryId,
          resolvedBy?.name || 'Hospital'
        );
      } catch (pushErr) {
        console.warn('Push notification failed:', pushErr);
      }
    } catch (emailErr) {
      console.warn('Email notification failed:', emailErr);
    }

    return { success: true, message: 'Query resolved and document updated' };
  } catch (error) {
    console.error('Error resolving document query:', error);
    return { success: false, error: error.message };
  }
};

// Get all queries for a specific application (optionally filter by documentKey)
export const getDocumentQueries = async (applicationId, documentKey = null) => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) return { success: false, error: 'Application not found' };
    const app = appResult.application;

    const queriesRef = collection(db, 'patientApplications', app.id, 'documentQueries');
    let q = query(queriesRef, orderBy('raisedAt', 'desc'));

    if (documentKey) {
      q = query(queriesRef, where('documentKey', '==', documentKey), orderBy('raisedAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    const queries = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    return { success: true, queries };
  } catch (error) {
    console.error('Error fetching document queries:', error);
    return { success: false, error: error.message };
  }
};

// Get count of open queries for an application
export const getOpenQueryCount = async (applicationId) => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) return { success: false, error: 'Application not found' };
    const app = appResult.application;

    const queriesRef = collection(db, 'patientApplications', app.id, 'documentQueries');
    const q = query(queriesRef, where('status', '==', 'open'));
    const snapshot = await getDocs(q);

    return { success: true, count: snapshot.size };
  } catch (error) {
    console.error('Error fetching open query count:', error);
    return { success: false, error: error.message };
  }
};
